import React from 'react';
import { useLandingPageContent } from '../hooks/useContentful';

const HeroSection = () => {
  const { content, loading, error } = useLandingPageContent();


  if (loading) {
    return (
      <section id="inicio" className="pt-24 md:pt-32 pb-12 md:pb-20 bg-gradient-to-br from-purple-50 via-white to-purple-100 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="lg:grid lg:grid-cols-2 lg:gap-12 items-center">
            {/* Text skeleton */}
            <div>
              <div className="h-10 md:h-12 bg-gradient-to-r from-purple-300 to-purple-400 rounded-lg w-3/4 mb-4 animate-shimmer-purple"></div>
              <div className="h-8 md:h-10 bg-gradient-to-r from-purple-200 to-purple-300 rounded-lg w-1/2 mb-8 animate-shimmer-purple" style={{ animationDelay: '0.2s' }}></div>
              <div className="space-y-3 mb-8">
                <div className="h-5 bg-gradient-to-r from-purple-100 to-purple-200 rounded w-full animate-shimmer-purple"></div>
                <div className="h-5 bg-gradient-to-r from-purple-100 to-purple-200 rounded w-5/6 animate-shimmer-purple"></div>
              </div>
              <div className="h-14 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full w-48 animate-pulse"></div>
            </div>
            {/* Image skeleton */}
            <div className="hidden lg:block">
              <div className="bg-gradient-to-br from-purple-200 via-purple-300 to-purple-200 rounded-2xl h-96 w-full animate-shimmer-purple shadow-xl"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    // Fallback to static content
  }

  return (
    <section id="inicio" className="pt-24 md:pt-32 pb-12 md:pb-20 bg-gradient-to-br from-purple-50 via-white to-pink-50 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-purple-200/20 rounded-full -mr-48 -mt-48 blur-3xl animate-float"></div>
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-pink-200/15 rounded-full -ml-36 -mb-36 blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>

      {/* Background Image */}
      <div className="absolute inset-0 z-0 opacity-10">
        <img
          src={content?.heroImage?.fields?.file?.url}
          alt="Fondo veterinaria"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="lg:grid lg:grid-cols-2 lg:gap-12 items-center">
          <div className="animate-fadeInLeft" style={{ animationDelay: '0.2s' }}>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-extrabold text-gray-800 mb-4 md:mb-6 leading-tight">
              {content?.heroTitle} <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 animate-pulse">{content?.titleHighlight}</span>
            </h1>

            <p className="text-base sm:text-lg text-gray-900 mb-6 md:mb-8 max-w-xl animate-fadeInLeft" style={{ animationDelay: '0.4s' }}>
              {content?.subtitle} <span className="text-purple-600">{content?.subtitleHighlight}</span>
            </p>

            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 animate-fadeInLeft" style={{ animationDelay: '0.6s' }}>
              <a
                href="#contacto"
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 md:px-10 py-3 md:py-4 rounded-full font-bold text-center text-base md:text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 relative overflow-hidden group"
              >
                <span className="relative z-10">{content?.ctaText} <i className="fas fa-phone-alt ml-2"></i></span>
                <div className="absolute inset-0 bg-gradient-to-r from-purple-700 to-purple-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </a>

              <a
                href="#servicios"
                className="border-2 border-purple-600 text-purple-600 hover:bg-gradient-to-r hover:from-purple-600 hover:to-pink-600 hover:text-white px-8 md:px-10 py-3 md:py-4 rounded-full font-bold text-center text-base md:text-lg transition-all duration-300 hover:scale-105 hover:shadow-lg"
              >
                Ver Servicios <i className="fas fa-arrow-right ml-2"></i>
              </a>
            </div>
          </div>

          {/* Side Image */}
          <div className="mt-8 lg:mt-0 animate-fadeInRight" style={{ animationDelay: '0.4s' }}>
            <div className="relative group">
              <img 
                src={content?.heroSideImage?.fields?.file?.url} 
                alt="Veterinario con mascota" 
                className="rounded-3xl shadow-2xl transform hover:scale-105 transition-all duration-500 relative z-10 w-full" 
              />
              {/* Floating elements */}
              <div className="hidden md:block absolute -top-4 -right-4 w-20 h-20 bg-purple-200/30 rounded-full animate-bounce" style={{ animationDelay: '1s' }}></div>
              <div className="hidden md:block absolute -bottom-6 -left-6 w-16 h-16 bg-pink-200/25 rounded-full animate-bounce" style={{ animationDelay: '1.5s' }}></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
